import React from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, Row, Col } from "reactstrap";

interface StartVisitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  assignedCount: number;
  visitNumber: string;
  isPending?: boolean;
}

const StartVisitModal: React.FC<StartVisitModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  assignedCount,
  visitNumber,
  isPending = false
}) => {
  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg" className="common_modal">
      <ModalHeader toggle={onClose} className="bg-light border-bottom p-3">
        <div className="d-flex align-items-center gap-2">
          <div style={{
            width: 36, height: 36, borderRadius: 10, background: "#eff6ff",
            display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bfdbfe"
          }}>
            <i className="ri-play-circle-line text-primary fs-18" />
          </div>
          <div>
            <h5 className="modal-title mb-0 fw-bold">Start Visit Session</h5>
            <span className="text-muted" style={{ fontSize: 12 }}>
              Visit {visitNumber}
            </span>
          </div>
        </div>
      </ModalHeader>
      <ModalBody className="p-4">
        {/* Comparison columns */}
        <Row className="g-3 mb-4">
          <Col md={6}>
            <div style={{
              background: "#f7fcf9",
              border: "1px solid #e6f4ed",
              borderRadius: 12,
              padding: 16,
              height: "100%"
            }}>
              <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom" style={{ borderColor: "#e6f4ed" }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%",
                  background: "#e8f7ec", display: "flex",
                  alignItems: "center", justifyContent: "center"
                }}>
                  <i className="ri-checkbox-circle-line text-success fs-16" />
                </div>
                <span className="fw-bold text-success-dark" style={{ fontSize: 14, color: "#1b5e20" }}>
                  After Visit Start – Available Actions
                </span>
              </div>
              <div className="d-flex flex-column gap-3">
                {[
                  { text: "Check-in subjects", icon: "ri-login-box-line" },
                  { text: "Check-out subjects", icon: "ri-logout-box-line" },
                  { text: "Perform & record activities", icon: "ri-task-line" },
                  { text: "Report Adverse Events", icon: "ri-alert-line" },
                  { text: "Withdraw / Dropout / Block subjects", icon: "ri-user-forbid-line" },
                  { text: "View visit details & reports", icon: "ri-file-text-line" },
                ].map((item, i) => (
                  <div key={i} className="d-flex align-items-center gap-2">
                    <div style={{
                      width: 26, height: 26, borderRadius: 6,
                      background: "#e8f5ed", display: "flex",
                      alignItems: "center", justifyContent: "center", flexShrink: 0
                    }}>
                      <i className={`${item.icon} text-success fs-14`} />
                    </div>
                    <span className="text-muted" style={{ fontSize: 13, color: "#374151" }}>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </Col>

          <Col md={6}>
            <div style={{
              background: "#fdf8f8",
              border: "1px solid #f9ecec",
              borderRadius: 12,
              padding: 16,
              height: "100%"
            }}>
              <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom" style={{ borderColor: "#f9ecec" }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%",
                  background: "#fae8e8", display: "flex",
                  alignItems: "center", justifyContent: "center"
                }}>
                  <i className="ri-lock-line text-danger fs-16" />
                </div>
                <span className="fw-bold text-danger-dark" style={{ fontSize: 14, color: "#b71c1c" }}>
                  After Visit Start – Restricted Actions
                </span>
              </div>
              <div className="d-flex flex-column gap-3">
                {[
                  { text: "Assign or remove subjects", icon: "ri-user-forbid-line" },
                  { text: "Edit visit details", icon: "ri-pencil-line" },
                  { text: "Edit activities", icon: "ri-list-settings-line" },
                  { text: "Change visit window or schedule", icon: "ri-calendar-2-line" },
                  { text: "Delete visit", icon: "ri-delete-bin-line" },
                ].map((item, i) => (
                  <div key={i} className="d-flex align-items-center gap-2">
                    <div style={{
                      width: 26, height: 26, borderRadius: 6,
                      background: "#fae8e8", display: "flex",
                      alignItems: "center", justifyContent: "center", flexShrink: 0
                    }}>
                      <i className={`${item.icon} text-danger fs-14`} />
                    </div>
                    <span className="text-muted" style={{ fontSize: 13, color: "#374151" }}>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </Col>
        </Row>

        {/* Assigned Subjects Stat */}
        <div className="border rounded-3 p-3 bg-light d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-3">
            <div className="p-2 bg-white rounded-circle border d-flex align-items-center justify-content-center" style={{ width: 42, height: 42 }}>
              <i className="ri-group-line text-primary fs-18" />
            </div>
            <div>
              <div className="fw-bold text-dark" style={{ fontSize: 14 }}>Assigned Subjects</div>
              <div className="text-muted small">Current enrollments for this session</div>
            </div>
          </div>
          <div className="text-end">
            <span className="badge bg-primary-subtle text-primary fw-bold px-3 py-2 rounded-pill fs-13">
              {assignedCount} {assignedCount === 1 ? "Subject" : "Subjects"}
            </span>
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="bg-white border-top p-3">
        <Button outline color="dark" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button color="primary" onClick={onConfirm} disabled={isPending} outline>
          {isPending && <Spinner size="sm" className="me-2" />}
          <i className="ri-play-line me-1" /> Start Visit
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default StartVisitModal;
